import { APIs } from "../../utils/worker";
import {
  PINCODE_MAP_ASSISTANT_API,
  PINCODE_MAP_ASSISTANT_LIMITS,
} from "../../constants/pincodeMapAssistantConstants";

const normalizeBaseUrl = (value) => String(value || "").trim().replace(/\/+$/, "");

const PINCODE_CHAT_BASE_URL = normalizeBaseUrl(
  process.env.REACT_APP_PINCODE_CHAT_BASE_URL || APIs?.bffBaseURL || ""
);

const { defaultTimeoutMs: DEFAULT_TIMEOUT_MS } = PINCODE_MAP_ASSISTANT_LIMITS;
const { askEndpoint: ASK_ENDPOINT } = PINCODE_MAP_ASSISTANT_API;

const parseJsonSafely = async (response) => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const getUserInfo = () => {
  try {
    const rawValue = localStorage.getItem(PINCODE_MAP_ASSISTANT_API.userInfoStorageKey);
    if (!rawValue || rawValue === PINCODE_MAP_ASSISTANT_API.undefinedStorageValue) return {};
    return JSON.parse(rawValue) || {};
  } catch {
    return {};
  }
};

const getClientId = () => {
  const userInfo = getUserInfo();
  return (
    userInfo?.userId ||
    userInfo?.id ||
    userInfo?.email ||
    userInfo?.username ||
    PINCODE_MAP_ASSISTANT_API.fallbackClientId
  );
};

const getRequestHeaders = (customHeaders = {}) => {
  const userInfo = getUserInfo();
  const headers = {
    "Content-Type": PINCODE_MAP_ASSISTANT_API.jsonContentType,
    [PINCODE_MAP_ASSISTANT_API.headerClientId]: getClientId(),
    ...customHeaders,
  };

  if (userInfo?.accessToken) {
    headers[PINCODE_MAP_ASSISTANT_API.headerAuthorization] = `${
      PINCODE_MAP_ASSISTANT_API.bearerPrefix
    } ${userInfo.accessToken}`;
  }

  return headers;
};

const requestWithTimeout = async (
  endpoint,
  {
    method = PINCODE_MAP_ASSISTANT_API.defaultMethod,
    body,
    headers,
    timeoutMs = DEFAULT_TIMEOUT_MS,
  } = {}
) => {
  if (!PINCODE_CHAT_BASE_URL) {
    throw new Error(PINCODE_MAP_ASSISTANT_API.errorBaseUrlNotConfigured);
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${PINCODE_CHAT_BASE_URL}${endpoint}`, {
      method,
      headers: getRequestHeaders(headers),
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });

    const payload = await parseJsonSafely(response);
    if (!response.ok) {
      throw new Error(
        payload?.error?.message ||
          payload?.message ||
          `${PINCODE_MAP_ASSISTANT_API.errorRequestFailedPrefix} (${response.status})`
      );
    }

    return payload;
  } catch (error) {
    if (error?.name === PINCODE_MAP_ASSISTANT_API.abortErrorName) {
      throw new Error(PINCODE_MAP_ASSISTANT_API.errorRequestTimedOut);
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }
};

export const askPincodeMapAssistant = async ({
  question,
  conversation = [],
  context = null,
  options = {},
}) =>
  requestWithTimeout(ASK_ENDPOINT, {
    method: PINCODE_MAP_ASSISTANT_API.postMethod,
    body: {
      question,
      conversation,
      context,
      options,
    },
  });
